import { useEffect, useState } from 'react';

import { useSession } from '@/lib/auth/session';

import {
  backupFor,
  backupOf,
  restoredFrom,
  shouldOfferBackup,
  useBuilderBackupStore,
} from './builderBackupStore';
import type { BuilderBackup } from './builderBackupStore';
import type { WorkflowDraft } from './types';
import { useDraft } from './useDraftContext';

/**
 * How long the draft has to sit still before it is backed up. Long enough not
 * to write on every keystroke of a rename, short enough that a crash loses a
 * moment rather than a minute; `pagehide` covers the tab closing inside it.
 */
const BACKUP_DELAY_MS = 500;

/**
 * The builder's browser backup (spec 144 US4): kept while there are unsaved
 * edits, offered back when it is newer than what the server holds, and never
 * applied unasked.
 *
 * The offer is decided once, against the draft as the server returned it. While
 * it stands the stored backup is left exactly as it is, even if the author
 * starts editing: a new backup written over it would throw away the very work
 * the banner is offering back.
 *
 * A clean draft has nothing to back up, so its backup goes — after a save, a
 * publish, a discard, or an undo back to what the server holds.
 *
 * Backups belong to the signed-in person (a browser can be shared). Until the
 * session says who that is, nothing is kept and nothing is offered; the offer
 * is decided the moment it does.
 */
export function useBuilderBackup(workflowId: string) {
  const { draft, edit } = useDraft();
  const owner = useSession().user?.id ?? null;
  const keep = useBuilderBackupStore((state) => state.keep);
  const drop = useBuilderBackupStore((state) => state.drop);

  const [decision, setDecision] = useState<{
    owner: string | null;
    offer: BuilderBackup | null;
  }>(() => ({ owner, offer: offerFor(owner, workflowId, draft) }));

  // Adjusted while rendering rather than in an effect, so the first render
  // with a known person already shows the right offer, and the backup effect
  // below never runs against a stale one and overwrites what is on offer.
  if (decision.owner !== owner) {
    setDecision({ owner, offer: offerFor(owner, workflowId, draft) });
  }
  const offer = decision.owner === owner ? decision.offer : null;

  useEffect(() => {
    if (owner === null || decision.owner !== owner || offer) return;
    if (!draft.dirty) {
      drop(owner, workflowId);
      return;
    }

    const write = () => keep(owner, workflowId, backupOf(draft, new Date()));
    const timer = window.setTimeout(write, BACKUP_DELAY_MS);
    // The debounce would otherwise lose the last half-second to a closed tab.
    window.addEventListener('pagehide', write);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pagehide', write);
    };
  }, [draft, offer, owner, decision.owner, workflowId, keep, drop]);

  return {
    /** The backup on offer, or `null`. */
    offer,
    /** Load the backup's edits as unsaved changes; one undo takes them back. */
    restore: () => {
      if (!offer) return;
      edit((current) => restoredFrom(current, offer));
      setDecision({ owner, offer: null });
    },
    throwAway: () => {
      if (owner !== null) drop(owner, workflowId);
      setDecision({ owner, offer: null });
    },
  };
}

/** `person`'s backup of the workflow, if it is worth offering against `draft`. */
function offerFor(
  person: string | null,
  workflowId: string,
  draft: WorkflowDraft,
): BuilderBackup | null {
  if (person === null) return null;
  const stored = backupFor(person, workflowId);
  return stored && shouldOfferBackup(stored, draft, new Date()) ? stored : null;
}
